import { FC, useCallback, useEffect, useRef } from 'react';

import { BarChart, LineChart, PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { dispose, type EChartsCoreOption, EChartsType, init } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { RendererType } from 'echarts/types/src/util/types.js';

import { useResizeCallback } from '@/hooks/use-resize-callback';

type Props = {
	className?: string;
	style?: React.CSSProperties;
	chartsRef?: React.Ref<EChartsCoreOption>;
	renderer?: RendererType;
};

echarts.use([
	PieChart,
	BarChart,
	LineChart,
	GridComponent,
	TooltipComponent,
	LegendComponent,
	CanvasRenderer,
]);

export const Echarts: FC<Props> = ({
	chartsRef,
	className,
	renderer = 'canvas',
	style,
}): JSX.Element => {
	const localChartsRef = useRef<EChartsType>();

	const refCallback = useCallback(
		(element: HTMLDivElement) => {
			if (element != null) {
				const charts = init(element, null, { renderer });

				//@ts-ignore
				if (typeof chartsRef === 'function') chartsRef(charts);
				//@ts-ignore
				else if (chartsRef != undefined) chartsRef.current = charts;
				localChartsRef.current = charts;
			} else {
				if (typeof chartsRef === 'function') chartsRef(null);
				//@ts-ignore
				else if (chartsRef != undefined) chartsRef.current = null;

				const charts = localChartsRef.current;
				if (charts != undefined) dispose(charts);
				localChartsRef.current = undefined;
			}
		},
		[chartsRef, renderer],
	);

	const [wrappedRefCallback, { width, height }] = useResizeCallback({
		ref: refCallback,
	});
	useEffect(() => localChartsRef.current?.resize(), [width, height]);

	return (
		<div
			ref={wrappedRefCallback}
			className={`echarts-react ${className}`}
			style={{
				display: 'flex',
				height: '100%',
				width: '100%',
				overflow: 'hidden',
				...style,
			}}
		/>
	);
};
